import { useState } from "react";
import { Alert, Button, Card, CardHeader, EmptyState, LoadingBlock, PageHeader } from "../components/ui";
import { CopyButton } from "../components/CopyButton";
import { Offer } from "../components/Offer";
import { MARKETING_URL } from "../lib/api";
import { useLinks, useMedia, usePromotions } from "../lib/queries";
import type { PromoImage } from "../lib/types";
import { Trans, useTranslation } from "react-i18next";

/**
 * Everything an affiliate promotes us with: the codes, and the images.
 *
 * The two belong together because they are used together — somebody composing a
 * post wants a picture and a reason to click it, and both are things we hand
 * them rather than things they earn. Keeping either on the overview meant that
 * screen grew with every campaign an operator released and every image the
 * brand added, on the one page whose job is to answer "have I earned anything".
 *
 * ---------------------------------------------------------------------------
 * Why this is a page and not a card
 * ---------------------------------------------------------------------------
 * It began as a card on the overview, under the offers, and it was wrong there
 * for a reason that only shows up once the images are real: six of them is most
 * of a screen. The overview is opened to see whether anything has been earned,
 * and a gallery sitting above the balance pushed the answer below the fold on a
 * screen whose whole argument is that the link comes first and the money is its
 * consequence.
 *
 * Frequency settles it. The link is wanted constantly, the offer occasionally,
 * and a picture only while somebody is actually composing a post — which is a
 * thing they come here to do, not something they need in their way the rest of
 * the time. The overview keeps a short strip so this is discoverable.
 *
 * ---------------------------------------------------------------------------
 * The warning between them is the point of the page
 * ---------------------------------------------------------------------------
 * **Neither a code nor an image carries the affiliate's link.** Both are the
 * same for everybody, so neither can hold a personal URL — and somebody who
 * arrives on a code alone is a customer we gained and an affiliate we did not
 * pay. Nothing on the first version of this said so, which made a well-meant
 * feature a way to lose a commission quietly.
 *
 * It sits between the two sections rather than above one, because it is true of
 * both, and the affiliate's own link is copyable from it.
 */
/**
 * Offers revealed per press.
 *
 * Four, because the grid is two columns and four is two complete rows. Five
 * left the last tile alone on a row with a hole beside it — and every press
 * after the first put it back, since an odd page size never divides an even
 * grid. A multiple of the column count is the only size that cannot do that.
 */
const PAGE = 4;

export function PromotePage() {
  const { t } = useTranslation("promote");
  const media = useMedia();
  const links = useLinks(false);
  const { data: promotions } = usePromotions();
  const [visible, setVisible] = useState(PAGE);

  /* The first live link, which is what `PrimaryLink` shows on the overview. */
  const primary = (links.data ?? []).find((link) => !link.archived);

  const offers = promotions ?? [];
  const shown = offers.slice(0, visible);
  const rest = offers.length - shown.length;

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
      />

      {/*
        A band rather than a card, and first rather than between the two
        sections it applies to.

        It was a full card with its own heading, which gave a one-sentence
        caveat the same weight as the things it is a caveat about — and put it
        halfway down, after somebody had already scrolled past the codes. It
        applies to everything below it, so it goes above everything below it,
        in the smallest shape that still gets read.
      */}
      <Alert tone="info">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span>
            <Trans ns="promote" i18nKey="putLink" components={{ strong: <strong className="font-medium" /> }} />
          </span>
          {primary ? (
            <span className="flex items-center gap-2">
              <code className="ident rounded bg-raised px-2 py-1 text-fg">{primary.url}</code>
              <CopyButton value={primary.url} label={t("copyLink")} />
            </span>
          ) : (
            <span className="text-warn">Make a link on Links first.</span>
          )}
        </div>
      </Alert>

      {/*
        Every released campaign, five at a time. The overview does not list them
        at all — this is the page somebody opened to read the list.

        Paged in the client rather than over the network, because the whole list
        already arrived: the endpoint returns at most `AFFILIATE_PROMOTION_LIMIT`
        of them in one response, and a request per five would be three round
        trips to re-read something held in memory. What the control is for is the
        page's height, not the payload's.
      */}
      {offers.length > 0 && (
        <Card>
          <CardHeader
            title={t("offer", { count: offers.length })}
            description={t("offersDescription")}
            action={
              offers.length > PAGE ? (
                <span className="text-sm text-fg-subtle tabular-nums">
                  {shown.length} of {offers.length}
                </span>
              ) : undefined
            }
          />
          <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
            {shown.map((promotion) => (
              <Offer key={promotion.code} promotion={promotion} />
            ))}
          </div>
          {rest > 0 && (
            <div className="border-t border-line px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setVisible(visible + PAGE)}>
                {t("showMore", { count: Math.min(rest, PAGE) })}
              </Button>
            </div>
          )}
        </Card>
      )}

      <Card>
        <CardHeader
          title={t("images")}
          description={t("imagesDescription")}
        />
        {media.isPending ? (
          <LoadingBlock label={t("loadingImages")} />
        ) : !MARKETING_URL || (media.data ?? []).length === 0 ? (
          <EmptyState
            title={t("noImages")}
            description={t("noImagesDescription")}
          />
        ) : (
          /*
           * Three across from `md` rather than `lg`. At two, a tile is half the
           * page wide and the gallery ran longer than the offers above it —
           * which inverts their importance, since a code is worth more than a
           * picture of one.
           */
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 md:grid-cols-3">
            {(media.data ?? []).map((image) => (
              <MediaTile key={image.name} image={image} />
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

/**
 * One image: a preview that opens it, what it is for, and its link.
 *
 * Every tile the same shape, with the image contained inside it. The six run
 * from a 728x90 strip to a 1080x1350 portrait, and letting each size its own
 * cell produced a grid with holes in it — a banner beside a portrait four times
 * its height, and the next row starting wherever the taller one ended. The real
 * dimensions are stated underneath, so showing them at a common size hides
 * nothing.
 *
 * "Open" rather than "Download", because that is what the anchor does: a
 * `download` attribute is ignored on a cross-origin URL, and these are served
 * from the marketing site.
 */
export function MediaTile({ image }: { image: PromoImage }) {
  const { t } = useTranslation("promote");
  const url = `${MARKETING_URL}/promo/${image.name}`;

  return (
    <figure className="space-y-2">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex aspect-[16/10] items-center justify-center overflow-hidden rounded-md border border-line bg-raised p-3 transition hover:border-line-strong"
      >
        {/* The intrinsic size is given so a tile does not resize as its image
            arrives, and `loading="lazy"` because most of these are below the
            fold on the page they are shown on. */}
        <img
          src={url}
          alt={t("imageAlt", { width: image.width, height: image.height })}
          width={image.width}
          height={image.height}
          loading="lazy"
          className="max-h-full max-w-full object-contain"
        />
      </a>
      <figcaption className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-[13px] text-fg">{image.use}</span>
        <span className="text-xs text-fg-subtle tabular-nums">
          {image.width} &times; {image.height}
        </span>
      </figcaption>
      <CopyButton value={url} label={t("copyLink")} />
    </figure>
  );
}
